import { Component, HostListener, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  LocalStorageService,
  ProjectManagerService,
  SessionManagerService,
  SessionTimeoutService,
  User,
  CookieService,
  Project,
  FiscalYearService,
} from '@ops/shared-types';
import { filter, pairwise, startWith, Subject, Subscription, takeUntil } from 'rxjs';
import { ProjectStatusGuardService } from '../../legacy/shared/shared-types/src/lib/services/project-status-guard.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private urSub!: Subscription;
  private uSub!: Subscription;
  private tfSub!: Subscription;
  private pmSub!: Subscription;
  private fySub!: Subscription;
  private projectStatusSub!: Subscription;
  private projectStatusFullPreventionSub!: Subscription;
  private sessionTimeoutSub!: Subscription;

  guestView = true;
  authPages = true;
  projectStatusPrevention = false;
  projectStatusFullPrevention = false;
  private destroy$ = new Subject<void>();
  constructor(
    private httpClient: HttpClient,
    private ls: LocalStorageService,
    public sm: SessionManagerService,
    private pm: ProjectManagerService,
    private router: Router,
    private translate: TranslateService,
    private cookieService: CookieService,
    public projectStatusGuard: ProjectStatusGuardService,
    private cdr: ChangeDetectorRef,
    private sessionTimeoutService: SessionTimeoutService,
    private fiscalYearService: FiscalYearService
  ) {
    this.sm.attemptResumeSession();
    this.initializeLanguage();
    this.initializeSessionTimeout();
  }

  private initializeLanguage(): void {
    // Set default language
    this.translate.setDefaultLang('en');

    // Check if there's a saved language preference in cookies
    const savedLanguage = this.cookieService.getLanguage();

    if (savedLanguage && this.isValidLanguage(savedLanguage)) {
      // Use the saved language
      this.translate.use(savedLanguage);
    } else {
      // Use English as fallback
      this.translate.use('en');
    }
  }

  private isValidLanguage(language: string): boolean {
    const validLanguages = ['en', 'jp', 'sr']; //'de', 'jp', 'sr'
    return validLanguages.includes(language);
  }

  private initializeSessionTimeout(): void {
    // Set up logout callback to avoid circular dependency
    this.sessionTimeoutService.setLogoutCallback(() => {
      // Use force logout for session timeouts to ensure proper redirect
      this.sessionTimeoutService.forceLogoutAndRedirect();
    });
    
    // Subscribe to session timeout events
    this.sessionTimeoutSub = this.sessionTimeoutService.getSessionActiveObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isActive: boolean) => {
        if (!isActive) {
          console.log('Session has been terminated due to inactivity');
          // The session timeout service will handle the logout automatically
        }
      });

    // Subscribe to time until timeout for potential UI updates
    this.sessionTimeoutService.getTimeUntilTimeoutObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((timeUntilTimeout: number) => {
        // You can use this to show a countdown or warning in the UI
        const minutesLeft = Math.ceil(timeUntilTimeout / (60 * 1000));
        const warningThreshold = this.sessionTimeoutService.getWarningTimeInMinutes();
        
        // Match the warning threshold from SessionTimeoutService
        if (minutesLeft <= warningThreshold && minutesLeft > 0) {
          console.warn(`Session will expire in ${minutesLeft} minutes due to inactivity`);
        }
      });
  }

  ngOnInit(): void {
    if (
      location.protocol !== 'https:' &&
      location.hostname !== 'localhost' &&
      location.host.indexOf('demo') === -1
    ) {
      window.location.href = `https:${window.location.href.substring(
        window.location.protocol.length
      )}`;

      this.httpClient.get('/region').subscribe((res: any) => {
        console.log(`%c🖥️ Frontend served from: ${res.region}`, 
          'color: #2196F3; font-weight: bold');
      });
    }

    this.tfSub = this.ls.getTimeoutFinishedObservable().subscribe(() => {
      // console.log('SHOULD TRACK HERE', this.ls.getCurrentCompany()?._id);

      if (this.pmSub?.unsubscribe) {
        this.pmSub.unsubscribe();
      }

      this.pmSub = this.pm
        .getAllProjects(this.ls.getCurrentCompany()?._id)
        .subscribe();

      if (this.fySub?.unsubscribe) {
        this.fySub.unsubscribe();
      }

      this.fySub = this.fiscalYearService
        .getFiscalYearsForCompany(this.ls.getCurrentCompany()?._id)
        .subscribe();
    });

    this.uSub = this.sm.getUserObservable().subscribe((user: User) => {
      this.authPages = !user?.accessToken;
    });

    this.urSub = this.sm
      .getUserRoleObservable()
      .subscribe((userRole: string) => {
        this.guestView = userRole === 'guest';
      });

    // Subscribe to project status prevention changes
    this.projectStatusSub = this.projectStatusGuard
      .getProjectStatusPrevention$()
      .subscribe((prevention: boolean) => {
        setTimeout(() => {
          this.projectStatusPrevention = prevention;
          this.cdr.detectChanges();
        });
      });

    // Subscribe to project status full prevention changes
    this.projectStatusFullPreventionSub = this.projectStatusGuard
      .getProjectStatusFullPrevention$()
      .subscribe((fullPrevention: boolean) => {
        setTimeout(() => {
          this.projectStatusFullPrevention = fullPrevention;
          this.cdr.detectChanges();
        });
      });

    // Subscribe to current project changes and update the project status guard
    this.pm.getCurrentPojectObservable().pipe(
      startWith(null),
        pairwise(),
        filter(([prev, curr]) => {
          // Only process if current is different from previous
          if (!curr) return false;
          if (!prev) return true;
          return prev._id !== curr._id || 
                 prev.status !== curr.status
        }),
        takeUntil(this.destroy$)
    ).subscribe(([prev, project]: [Project | null, Project]) => {
      // console.log('app component project');

      if (!project) {
        return;
      }

      this.projectStatusGuard.setCurrentProject(project);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.urSub?.unsubscribe();
    this.uSub?.unsubscribe();
    this.tfSub?.unsubscribe();
    this.pmSub?.unsubscribe();
    this.projectStatusSub?.unsubscribe();
    this.projectStatusFullPreventionSub?.unsubscribe();
    this.sessionTimeoutSub?.unsubscribe();
    this.authPages = true;
    this.guestView = true;
    this.projectStatusPrevention = false;
    this.projectStatusFullPrevention = false;
  }

  @HostListener('window:keydown.tab', ['$event'])
  onTabPrevent(event: Event) {
    const routerPath = this.router.url;
    const excludeFromTabOnGuest = [
      'login',
      'register',
      'welcome',
      'verify',
      'forgot-password',
      'resetPassword',
    ];

    if (
      (this.guestView || this.projectStatusPrevention || this.projectStatusFullPrevention) &&
      !excludeFromTabOnGuest.some((path) => routerPath.includes(path))
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  // getAllProjects(): Observable<Project[]> {
  //   return this.pm.getAllProjects(this.sm.getCurrentCompany()).pipe(
  //     switchMap((projects) => {
  //       this.ls.setAllProjects(projects);
  //       return of(projects);
  //     })
  //   );
  // }

  @HostListener('window:beforeunload', ['$event'])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async clearAllProjects($event: any): Promise<void> {
    await this.ls.clearEssential();
    this.ls.clearBankAccountCalculatedHistory();
    this.authPages = true;
    this.guestView = true;
    this.projectStatusPrevention = false;
    this.projectStatusFullPrevention = false;
  }

  @HostListener('window:load', ['$event'])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onPageLoad(event: Event) {
    if (this.sm.getUser()?.accessToken) {
      if (this.ls.getCurrentCompany()?._id) {
        this.fiscalYearService
          .getFiscalYearsForCompany(this.ls.getCurrentCompany()?._id)
          .subscribe();
        this.pm.getAllProjects(this.ls.getCurrentCompany()?._id).subscribe();
      }

      if (window?.location?.href?.toString()?.includes('company-finance')) {
        setTimeout(() => {
          this.router.navigate(['company-finance']);
        }, 50);
      }

      if (window?.location?.href?.toString()?.includes('project-finance')) {
        setTimeout(() => {
          this.router.navigate(['project-finance']);
        }, 50);
      }

      if (window?.location?.href?.toString()?.includes('cashflow')) {
        setTimeout(() => {
          this.router.navigate(['cashflow']);
        }, 50);
      }
    }
  }
}
